import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { matchCommands } from '../lib/commands'
import { recentMattersTracker, recentClientsTracker, recentContactsTracker } from '../lib/recentItems'
import { safeOpenPopupWindow } from '../services/token'

// Highlight the query substring within a result title/subtitle.  Returns
// an array of React nodes with the match wrapped in <mark>, safe against
// re-rendering (uses key based on index).  Case-insensitive.
function highlightMatch(text, query) {
  if (!text || !query || query.length < 2) return text
  const t = String(text)
  const q = String(query).trim()
  if (!q) return t
  const lower = t.toLowerCase()
  const lowerQ = q.toLowerCase()
  const parts = []
  let last = 0
  let idx = lower.indexOf(lowerQ)
  while (idx !== -1) {
    if (idx > last) parts.push(t.slice(last, idx))
    parts.push(
      <mark key={idx} className="bg-yellow-200 text-gray-900 font-semibold rounded px-0.5">
        {t.slice(idx, idx + q.length)}
      </mark>
    )
    last = idx + q.length
    idx = lower.indexOf(lowerQ, last)
  }
  if (last < t.length) parts.push(t.slice(last))
  return parts
}

const ICON = {
  client:        '👥',
  matter:        '📁',
  contact:       '📇',
  invoice:       '💰',
  time_entry:    '⏱',
  event:         '📅',
  communication: '📞',
  note:          '📝',
  // Ship 7b F2.2 audit follow-up (H-CR6): backend /api/search now dispatches
  // courts / judges / deadlines results too; UI needs an icon for each.
  court:         '🏛',
  judge:         '⚖️',
  deadline:      '📋',
}

const SECTION_LABEL = {
  clients:        'Clients',
  matters:        'Matters',
  contacts:       'Contacts',
  invoices:       'Invoices',
  time_entries:   'Time Entries',
  events:         'Events',
  communications: 'Communications',
  notes:          'Notes',
  // Ship 7b F2.2 audit follow-up (H-CR6): section headers for the new
  // court-rules-tier surfaces.
  courts:         'Courts',
  judges:         'Judges',
  deadlines:      'Deadlines',
}

const RECENT_KEY = 'lextrack_recent_searches'

// Type-prefix syntax: users can scope the palette to one type by prefixing
// their query with a short marker.  Reduces flood when a user knows what
// kind of record they're after ("m: smith" only shows matter results).
// The prefix is stripped from the query the server sees; the type filter
// is passed as a separate query param.  See backend search.js for the
// gate.  Prefix map is deliberately short; single-char to type mapping
// keeps typing fast.  `co:` needs the two-char form to disambiguate from
// `c:` (clients).
const TYPE_PREFIXES = [
  { prefix: 'c:',  type: 'clients',        label: 'clients only'   },
  { prefix: 'm:',  type: 'matters',        label: 'matters only'   },
  { prefix: 'co:', type: 'contacts',       label: 'contacts only'  },
  { prefix: 'i:',  type: 'invoices',       label: 'invoices only'  },
  { prefix: 't:',  type: 'time_entries',   label: 'time only'      },
  { prefix: 'e:',  type: 'events',         label: 'events only'    },
  { prefix: 'k:',  type: 'communications', label: 'comms only'     },
  { prefix: 'n:',  type: 'notes',          label: 'notes only'     },
]
// Extract prefix + stripped query.  Case-insensitive.  Longest prefix wins
// (`co:` before `c:`) so contacts scoping doesn't accidentally trip clients.
function parsePrefix(raw) {
  const lower = raw.toLowerCase()
  const hits = TYPE_PREFIXES.filter((p) => lower.startsWith(p.prefix))
  if (hits.length === 0) return { type: null, cleanQ: raw }
  const best = hits.sort((a, b) => b.prefix.length - a.prefix.length)[0]
  return { type: best.type, cleanQ: raw.slice(best.prefix.length).trim(), label: best.label }
}

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]') } catch { return [] }
}
function saveRecent(q) {
  const cur = loadRecent().filter(x => x !== q)
  const next = [q, ...cur].slice(0, 5)
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)) } catch {}
}

export default function GlobalSearch() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [q, setQ] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const inputRef = useRef(null)
  const wrapperRef = useRef(null)
  const debounceRef = useRef(null)

  // Detect + strip type-prefix syntax before running commands / recent-item
  // matching / API search.  The visible input still shows the raw text so the
  // user sees their prefix as they type it.
  const parsed = parsePrefix(q.trim())
  const searchQ = parsed.cleanQ

  // Matched commands for the current query — top of the palette above records.
  // Commands don't respect the type filter (commands are cross-cutting).
  const commands = searchQ.length >= 2 && !parsed.type ? matchCommands(user, searchQ, 5) : []

  // Recent items (jump-to targets) — sourced from the same trackers the
  // sidebar renders.  When the input is empty, we show the top ~5 across
  // all three types as clickable jumps.  When the user types, recent items
  // whose title contains the query bubble to the top (before API results)
  // so muscle-memory doesn't force a round-trip.
  const recentJumpItems = (() => {
    const qLower = searchQ.toLowerCase()
    // Type-tagged so we can render icons + navigate correctly.  Recent-items
    // respect type filters — a `m:` prefix hides clients + contacts here too.
    let all = [
      ...recentMattersTracker.get().map((r)  => ({ ...r, __recent: true, __kind: 'matter'  })),
      ...recentClientsTracker.get().map((r)  => ({ ...r, __recent: true, __kind: 'client'  })),
      ...recentContactsTracker.get().map((r) => ({ ...r, __recent: true, __kind: 'contact' })),
    ].sort((a, b) => (b.viewedAt || 0) - (a.viewedAt || 0))
    if (parsed.type === 'matters')  all = all.filter((r) => r.__kind === 'matter')
    if (parsed.type === 'clients')  all = all.filter((r) => r.__kind === 'client')
    if (parsed.type === 'contacts') all = all.filter((r) => r.__kind === 'contact')
    // Any other type filter (invoices/events/comms/notes/time) hides recent-items entirely
    // since the trackers don't cover those record types.
    if (parsed.type && !['matters','clients','contacts'].includes(parsed.type)) all = []
    if (qLower.length < 2) return all.slice(0, 5)
    return all.filter((r) => (r.title || '').toLowerCase().includes(qLower)).slice(0, 4)
  })()

  // Flat list — commands FIRST, then recent-item jumps, then API record hits.
  // This ordering makes Enter default to the most useful action.
  const flatResults = []
  commands.forEach((c) => flatResults.push({ __command: true, ...c }))
  recentJumpItems.forEach((r) => flatResults.push(r))
  if (data?.results) {
    Object.entries(data.results).forEach(([key, items]) => {
      items.forEach(item => flatResults.push(item))
    })
  }

  const runCommand = useCallback((cmd) => {
    if (!cmd) return
    saveRecent(q)
    setOpen(false)
    setQ('')
    try {
      cmd.run({
        navigate,
        dispatch: (name, detail) => window.dispatchEvent(
          detail ? new CustomEvent(name, { detail }) : new Event(name)
        ),
        logout
      })
    } catch (e) { console.error('command failed', cmd.id, e) }
  }, [navigate, q, logout])

  // Debounced search — tightened 300ms → 200ms for snappier feel; API is
  // fast enough (each branch is <20ms with the indexes) that the network
  // round-trip is the bottleneck, not the DB.
  useEffect(() => {
    clearTimeout(debounceRef.current)
    if (searchQ.length < 2) { setData(null); return }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const params = { q: searchQ }
        if (parsed.type) params.type = parsed.type
        const res = await api.get('/api/search', { params })
        setData(res.data)
        setHighlighted(0)
      } catch { setData(null) }
      setLoading(false)
    }, 200)
    return () => clearTimeout(debounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, parsed.type])

  // Focus listener for Ctrl+K
  useEffect(() => {
    const focus = () => { inputRef.current?.focus(); inputRef.current?.select(); setOpen(true) }
    window.addEventListener('focus-global-search', focus)
    return () => window.removeEventListener('focus-global-search', focus)
  }, [])

  // Close on outside click
  useEffect(() => {
    const fn = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  // Open in a pop-out Electron window instead of navigating in the current
  // window.  Silently falls back to same-window navigation on browsers (or
  // if the safeOpenPopupWindow helper isn't loaded).  Same popupKey format
  // as other pop-out surfaces so re-selecting the same result focuses the
  // existing window instead of duplicating.
  const openInPopup = useCallback((item) => {
    if (!item || !item.url) return false
    const url = item.__recent
      ? (item.__kind === 'matter' ? `/matters/${item.id}` : item.__kind === 'client' ? `/clients/${item.id}` : '/contacts')
      : item.url
    // Route to the popup mirror route (/popup/page/...) that PopupLayout renders.
    const popupRoute = `/popup/page${url}`
    const popupKey = `search-${item.type || item.__kind || 'result'}-${item.id || url}`
    try {
      const ok = safeOpenPopupWindow(popupKey, popupRoute, { width: 1200, height: 800 })
      return !!ok
    } catch { return false }
  }, [])

  const goto = useCallback((item, opts = {}) => {
    if (!item) return
    if (item.__command) { runCommand(item); return }
    // Ctrl/Cmd+Enter opens the result in a pop-out window (Electron only).
    // Falls back to same-window navigation if the popup helper isn't there.
    if (opts.popOut && openInPopup(item)) {
      setOpen(false); setQ('')
      return
    }
    if (item.__recent) {
      // Recent-item jump — route by tracker kind since not every type has a
      // detail page (contacts land on the list).
      setOpen(false); setQ('')
      const url = item.__kind === 'matter'  ? `/matters/${item.id}`
                : item.__kind === 'client'  ? `/clients/${item.id}`
                : '/contacts'
      navigate(url); return
    }
    saveRecent(q)
    setOpen(false)
    setQ('')
    navigate(item.url)
  }, [navigate, q, runCommand, openInPopup])

  const onKeyDown = (e) => {
    // Claimed (preventDefault): an Esc that leaves the search box must not
    // also take Calendar out of full screen (its window listener checks).
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); e.currentTarget.blur(); return }
    if (!open || flatResults.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, flatResults.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
    // Home / End for fast jumps through long result lists
    if (e.key === 'Home') { e.preventDefault(); setHighlighted(0) }
    if (e.key === 'End')  { e.preventDefault(); setHighlighted(flatResults.length - 1) }
    if (e.key === 'Enter') {
      e.preventDefault()
      const popOut = (e.ctrlKey || e.metaKey) && !!window.electronAPI
      if (flatResults[highlighted]) goto(flatResults[highlighted], { popOut })
      else if (searchQ) { saveRecent(searchQ); setOpen(false); navigate(`/search?q=${encodeURIComponent(searchQ)}`) }
    }
  }

  const recent = loadRecent()

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-300 text-sm pointer-events-none">🔍</span>
        <input
          id="global-search"
          ref={inputRef}
          type="text"
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search…"
          aria-label="Global search"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="global-search-listbox"
          className="w-full pl-9 pr-14 py-2 bg-primary-800 text-white placeholder-primary-300 text-sm rounded-lg border border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
        <kbd className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-sans text-primary-300 bg-primary-900/60 border border-primary-600 rounded px-1.5 py-0.5 pointer-events-none leading-none">⌘K</kbd>
      </div>

      {/* Screen-reader-only live region — announces result counts as the
          caller types.  Sighted users get the same info from the section
          headers; SR users would otherwise be told nothing about "12
          matters, 3 clients found" until they arrow-key through it.  We
          announce a compact summary so the reading doesn't get spammy on
          every keystroke — the polite region rate-limits itself. */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {open && loading && q.trim().length >= 2 && `Searching for "${q}"…`}
        {open && !loading && data && data.total > 0 &&
          `${data.total} result${data.total === 1 ? '' : 's'} for "${searchQ}"`}
        {open && !loading && data && data.total === 0 && q.trim().length >= 2 &&
          `No results for "${q}"`}
      </div>

      {open && (
        <div id="global-search-listbox"
             className="absolute left-0 right-0 top-full mt-1 bg-white text-gray-800 border border-gray-200 rounded-xl shadow-lg max-h-[70vh] overflow-y-auto z-50"
             role="listbox" aria-label="Search results">
          {/* Type-filter chip — only when a `c:` / `m:` / etc. prefix is active */}
          {parsed.type && (
            <div className="px-3 py-2 bg-primary-50 border-b border-primary-100 flex items-center justify-between gap-2">
              <span className="text-xs text-primary-700">
                <span className="font-semibold">Filter:</span> {parsed.label}
              </span>
              <button type="button"
                onClick={() => { setQ(searchQ); inputRef.current?.focus() }}
                className="text-[11px] text-primary-700 hover:underline">
                ✕ clear
              </button>
            </div>
          )}
          {loading && <div className="p-4 text-sm text-gray-400">Searching…</div>}

          {/* Empty-input state — show Recent items (jump-to targets) instead
              of just past search strings.  Falls back to past searches
              underneath when the user actually types nothing. */}
          {!loading && q.trim().length < 2 && (
            (recentJumpItems.length > 0 || recent.length > 0) ? (
              <div>
                {recentJumpItems.length > 0 && (
                  <div className="border-b border-gray-100">
                    <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">🕒 Recently opened</p>
                    {recentJumpItems.map((r) => {
                      const idx = flatResults.findIndex((x) => x.__recent && x.__kind === r.__kind && x.id === r.id)
                      const active = idx === highlighted
                      const icon = r.__kind === 'matter' ? '📁' : r.__kind === 'client' ? '👥' : '📇'
                      return (
                        <button key={`${r.__kind}-${r.id}`}
                          onClick={() => goto(r)}
                          onMouseEnter={() => setHighlighted(idx)}
                          className={`w-full text-left px-4 py-2 text-sm flex items-start gap-2 ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                          <span className="shrink-0 mt-0.5">{icon}</span>
                          <span className="flex-1 min-w-0 truncate">{r.title}</span>
                          <span className="text-gray-300 shrink-0 text-xs">↵</span>
                        </button>
                      )
                    })}
                  </div>
                )}
                {recent.length > 0 && (
                  <div>
                    <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Recent searches</p>
                    {recent.map((r, i) => (
                      <button key={i} onClick={() => setQ(r)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                        🔍 {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-sm text-gray-400">Type at least 2 characters to search…</div>
            )
          )}

          {/* When the user IS typing (>=2 chars), surface recent-item matches
              first so muscle-memory ("Ctrl+K → smith") doesn't have to wait
              for the API. */}
          {!loading && q.trim().length >= 2 && recentJumpItems.length > 0 && (
            <div className="border-b border-gray-100">
              <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">🕒 From recent</p>
              {recentJumpItems.map((r) => {
                const idx = flatResults.findIndex((x) => x.__recent && x.__kind === r.__kind && x.id === r.id)
                const active = idx === highlighted
                const icon = r.__kind === 'matter' ? '📁' : r.__kind === 'client' ? '👥' : '📇'
                return (
                  <button key={`${r.__kind}-${r.id}`}
                    onClick={() => goto(r)}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={`w-full text-left px-4 py-2 text-sm flex items-start gap-2 ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                    <span className="shrink-0 mt-0.5">{icon}</span>
                    <span className="flex-1 min-w-0 truncate">{r.title}</span>
                    <span className="text-gray-300 shrink-0 text-xs">↵</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Command palette actions — top of the results, above records */}
          {!loading && commands.length > 0 && (
            <div className="border-b border-gray-100">
              <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                ⚡ Actions
              </p>
              {commands.map((cmd) => {
                const idx = flatResults.findIndex((r) => r.__command && r.id === cmd.id)
                const active = idx === highlighted
                return (
                  <button key={cmd.id}
                    onClick={() => runCommand(cmd)}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={`w-full text-left px-4 py-2 text-sm flex items-start gap-2 ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    <span className="shrink-0 mt-0.5">{cmd.icon}</span>
                    <span className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{cmd.label}</p>
                      {cmd.hint && <p className="text-xs text-gray-500 truncate">{cmd.hint}</p>}
                    </span>
                    <span className="text-blue-400 shrink-0 text-xs mt-1">↵</span>
                  </button>
                )
              })}
            </div>
          )}

          {!loading && data && data.total === 0 && commands.length === 0 && recentJumpItems.length === 0 && q.trim().length >= 2 && (
            <div className="p-4 text-sm text-gray-500">No results for &ldquo;{q}&rdquo; — try different keywords.</div>
          )}

          {!loading && data && data.total > 0 && Object.entries(data.results).map(([key, items]) => {
            if (!items.length) return null
            return (
              <div key={key} className="border-b border-gray-100 last:border-b-0">
                <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <span>{ICON[items[0].type]} {SECTION_LABEL[key]}</span>
                  <span className="text-gray-300 font-normal normal-case">· {items.length}</span>
                </p>
                {items.map(item => {
                  const idx = flatResults.indexOf(item)
                  const active = idx === highlighted
                  return (
                    <button key={`${key}-${item.id}`}
                      onClick={(e) => goto(item, { popOut: (e.ctrlKey || e.metaKey) && !!window.electronAPI })}
                      onMouseEnter={() => setHighlighted(idx)}
                      className={`w-full text-left px-4 py-2 text-sm flex items-start gap-2 ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                      <span className="shrink-0 mt-0.5">{ICON[item.type]}</span>
                      <span className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{highlightMatch(item.title, searchQ)}</p>
                        {item.subtitle && <p className="text-xs text-gray-500 truncate">{highlightMatch(item.subtitle, searchQ)}</p>}
                      </span>
                      <span className="text-gray-300 shrink-0">→</span>
                    </button>
                  )
                })}
              </div>
            )
          })}

          {/* Footer hint — keyboard shortcuts + prefix syntax help.  Only when
              there ARE results (avoids clutter when we're showing the empty state). */}
          {!loading && ((data && data.total > 0) || commands.length > 0 || recentJumpItems.length > 0) && (
            <div className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100 flex items-center justify-between gap-2 flex-wrap">
              <span>
                <kbd className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1">↵</kbd> open
                {window.electronAPI && (
                  <> · <kbd className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1">⌘↵</kbd> pop out</>
                )}
                {!parsed.type && (
                  <> · Try <kbd className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1">m:</kbd>, <kbd className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1">c:</kbd>, <kbd className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1">i:</kbd> to filter</>
                )}
              </span>
            </div>
          )}

          {!loading && data && data.total > 0 && (
            <button
              onClick={() => {
                saveRecent(searchQ); setOpen(false)
                const params = new URLSearchParams({ q: searchQ })
                if (parsed.type) params.set('type', parsed.type)
                navigate(`/search?${params.toString()}`)
              }}
              className="w-full text-center px-4 py-2 text-xs text-primary-600 font-medium hover:bg-primary-50 border-t border-gray-100">
              View all {data.total} results →
            </button>
          )}
        </div>
      )}
    </div>
  )
}
